// frontend/src/components/auth/ProtectedRoute.tsx
// ENHANCED: Better role-based routing with automatic layout detection

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import SmartLayout from '@/components/Layout/SmartLayout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  layout?:
    | "admin"
    | "provider"
    | "customer"
    | "customer-account"
    | "public"
    | "quick-booking";
  priority?: number; // For handling route conflicts
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  layout,
  priority = 0,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Enhanced role checking - check if user has ANY of the required roles
  if (requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.includes(user.role);
    
    if (!hasRequiredRole) {
      // Redirect to appropriate dashboard based on user role
      const redirectPath = getRoleBasedRedirectPath(user.role);
      return <Navigate to={redirectPath} replace />;
    }
  }

  // Auto-detect layout based on user role if not specified
  const finalLayout = layout || getLayoutForUserRole(user?.role);

  return <SmartLayout forceLayout={finalLayout}>{children}</SmartLayout>;
};

// Helper function to get redirect path based on user role
const getRoleBasedRedirectPath = (userRole: string): string => {
  switch (userRole) {
    case 'admin':
    case 'super_admin':
      return '/dashboard';
    case 'provider':
      return '/dashboard';
    case 'user':
    case 'customer':
      return '/account';
    default:
      return '/login';
  }
};

// Helper function to auto-detect layout based on user role
const getLayoutForUserRole = (userRole?: string): 
  | "admin"
  | "provider" 
  | "customer"
  | "customer-account"
  | "public" => {
  
  switch (userRole) {
    case 'admin':
    case 'super_admin':
      return 'admin';
    case 'provider':
      return 'provider';
    case 'user':
    case 'customer':
      return 'customer';
    default:
      return 'public';
  }
};

export default ProtectedRoute;